import React, {
  FC,
  useMemo,
}                       from 'react';
import {
  Form,
  Field,
  useFormikContext,
}                       from 'formik';
import styled, { css }  from 'styled-components';
import { Grid }         from '@material-ui/core';
import { Breakpoint }   from '@material-ui/core/styles/createBreakpoints';
import {
  ICardAdValues,
  ICardAd,
}                       from '@typings/nationalAdManagement';
import { EAdType }      from '@enums/nationalAdManagement';
import { EUploadTypes } from '@enums/common';
import {
  Button,
  Switch,
  InputHint,
  TextInput,
  FileUpload,
  RequiredLabel,
}                       from '@components/index';
import { AdPreview }    from './AdPreview';

interface IStyledProps {
  selected: boolean;
}

const Wrapper = styled.div`
  width   : 100%;
  padding : 30px 30px 50px 30px;

  @media (min-width: 600px) {
    padding: 30px 60px 50px 60px;
  }

  @media (min-width: 960px) {
    padding: 30px 60px 50px 100px;
  }
`;

const ActionsWrapper = styled.div`
  display          : flex;
  justify-content  : center;
  align-items      : center;
  margin           : 40px auto;
  background-color : ${({ theme: { width, backgroundColor } }): string =>
    (!['lg', 'xl'].includes(width) ? backgroundColor.lightGray : backgroundColor.white)};

  button {
    margin: 0 10px;
  }

  ${({ theme: { width } }) => (width === 'xs' && css`
    flex-direction: column;

    button:first-child {
      margin-bottom: 20px;
    }
  `)};
`;

const TypesTabsWrapper = styled.div`
  display        : flex;
  flex-direction : ${({ theme: { width } }): false | string => (['xs'].includes(width) && 'column')};
  margin         : 0 auto 30px;
  ${({ theme: { width } }): false | string => (['sm'].includes(width) && 'width: fit-content;')};
  ${({ theme: { width } }): false | string => (!['xs', 'sm'].includes(width) && 'margin-bottom: 80px')};

  > div {
    width       : ${({ theme: { width } }): string => (['xs'].includes(width) ?  '100%' : '240px')};
    text-align  : center;
    white-space : nowrap;
    padding-top : 6px;
    border      : 1px solid #0063A5;
  }

  div:first-child {
    ${({ theme: { width } }): string => (['xs'].includes(width) ? 'border-bottom: none' : 'border-right: none')};
    border-radius : ${({ theme: { width } }): string => (['xs'].includes(width) ? '5px 5px 0 0' : '5px 0 0 5px')};
  }

  div:last-child {
    ${({ theme: { width } }): string => (['xs'].includes(width) ? 'border-top: none' : 'border-left: none')};
    border-radius : ${({ theme: { width } }): string => (['xs'].includes(width) ? '0 0 5px 5px' : '0 5px 5px 0')};
  }
`;

const ActiveTabsWrapper = styled.div`
  display       : flex;
  margin        : 9px auto 30px;
  ${({ theme: { width } }): string => (['xs'].includes(width) ? 'border-top: none' : 'border-left: none')};
  border-radius : ${({ theme: { width } }): string => (['xs'].includes(width) ? '0 0 5px 5px' : '0 5px 5px 0')};

  > div {
    width       : 140px;
    text-align  : center;
    white-space : nowrap;
    height      : 32px;
    padding-top : 4px;
    border      : 1px solid #0063A5;
  }

  div:first-child {
    border-radius: 5px 0 0 5px;
  }

  div:last-child {
    border-radius: 0 5px 5px 0;
  }
`;

const Tab = styled.div<IStyledProps>`
  font-size        : 16px;
  cursor           : pointer;
  height           : 35px;
  padding-top      : 8px;
  padding-bottom   : 10px;
  color            : ${({ selected, theme: { textColor } }): string => (
    selected ? textColor.white : textColor.secondary)};
  background-color : ${({ selected, theme: { backgroundColor } }): string => (
    selected ? backgroundColor.darkBlue : backgroundColor.white)};
`;

const GridGray = styled(Grid)`
  background-color: ${({ theme: { backgroundColor } }): string => backgroundColor.lightGray};
`;

const InputWrapperPadded = styled.div`
  padding: 8px 0 28px;
`;

const UploadImageWrapper = styled.div`
  padding: 30px 0;
`;

const InputWrapper = styled.div`
  margin-bottom: 25px;
`;

interface IAdEditorFormProps {
  width    : Breakpoint;
  onCancel : () => void;
  logoUrl? : string;
}

export const AdEditorForm: FC<IAdEditorFormProps> = ({ width, onCancel, logoUrl }) => {
  const {
    values,
    touched,
    errors,
    isValid,
    dirty,
    isSubmitting,
    setFieldValue,
  } = useFormikContext<ICardAdValues>();

  const url = useMemo(
    () => (values.image
      ? window.URL.createObjectURL(values.image)
      : undefined
    ), [values.image],
  );

  const { image, ...restValues } = values;

  const adData = useMemo<Partial<ICardAd>>(
    () => ({
      ...restValues,
      imageUrl: url,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [restValues], // if we add url to the dependencies, we have unnecessary renders
  );

  return (
    <Form>
      <Grid container>
        <GridGray item xs={12} lg={8}>
          <Wrapper>
            <TypesTabsWrapper>
              <Tab
                selected = {values.type === EAdType.TextAndGraphicAd}
                onClick  = {(): void => setFieldValue('type', EAdType.TextAndGraphicAd)}
              >
                Text and Graphic Ad
              </Tab>
              <Tab
                selected = {values.type === EAdType.GraphicOnlyAd}
                onClick  = {(): void => setFieldValue('type', EAdType.GraphicOnlyAd)}
              >
                Graphic Only Ad
              </Tab>
            </TypesTabsWrapper>

            <Grid container spacing={5}>
              <Grid item xs={12} md={6} lg={12}>
                <Grid container spacing={5}>
                  <Grid item xs={12} sm={12} md={12} lg={6}>
                    <ActiveTabsWrapper>
                      <Tab
                        selected = {values.isActive}
                        onClick  = {(): void => setFieldValue('isActive', true)}
                      >
                        Ad Running
                      </Tab>
                      <Tab
                        selected = {!values.isActive}
                        onClick  = {(): void => setFieldValue('isActive', false)}
                      >
                        Suspend Ad
                      </Tab>
                    </ActiveTabsWrapper>

                    {values.type !== EAdType.GraphicOnlyAd && (
                      <>
                        <InputWrapper>
                          <Field
                            id         = "title"
                            name       = "title"
                            type       = "text"
                            label      = "Title Text"
                            error      = {touched.title && !!errors.title}
                            component  = {TextInput}
                            inputProps = {{ maxLength: 30 }}
                          />
                          <InputHint
                            text  = "30 Character Max"
                            error = {errors.title}
                          />
                          <RequiredLabel />
                        </InputWrapper>

                        <InputWrapper>
                          <Field
                            id         = "body"
                            name       = "body"
                            type       = "text"
                            label      = "Body Text"
                            error      = {touched.body && !!errors.body}
                            component  = {TextInput}
                            rows       = {7}
                            rowsMax    = {10}
                            inputProps = {{ maxLength: values.isClickable ? 135 : 225 }}
                            multiline
                          />
                          <InputHint
                            text  = {values.isClickable ? '135 Character Max' : '225 Character Max'}
                            error = {errors.body}
                          />
                          <RequiredLabel />
                        </InputWrapper>
                      </>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={12} md={12} lg={6}>
                    <InputWrapperPadded>
                      <Field
                        id        = "isClickable"
                        name      = "isClickable"
                        label     = "Indicate if this ad will be clickable."
                        component = {Switch}
                      />
                    </InputWrapperPadded>

                    {values.isClickable && (
                      <>
                        <InputWrapper>
                          <Field
                            id         = "link"
                            name       = "link"
                            type       = "text"
                            label      = "Valid URL link"
                            error      = {touched.link && !!errors.link}
                            inputProps = {{ maxLength: 255 }}
                            component  = {TextInput}
                          />
                          <RequiredLabel />
                        </InputWrapper>

                        <InputWrapper>
                          <Field
                            id         = "buttonName"
                            name       = "buttonName"
                            type       = "text"
                            label      = "Button Text"
                            error      = {touched.buttonName && !!errors.buttonName}
                            component  = {TextInput}
                            inputProps = {{ maxLength: 25 }}
                          />
                          <InputHint
                            text  = "25 Character Max"
                            error = {errors.buttonName}
                          />
                          <RequiredLabel />
                        </InputWrapper>
                      </>
                    )}

                    {values.type !== EAdType.TextOnlyAd && (
                      <UploadImageWrapper>
                        <FileUpload
                          id        = "image"
                          name      = "image"
                          label     = "Choose file"
                          fileType  = {EUploadTypes.Image}
                          withSize
                        />
                      </UploadImageWrapper>
                    )}
                  </Grid>
                </Grid>
              </Grid>

              {(width !== 'lg' && width !== 'xl') && (
                <Grid item xs={12} md={6}>
                  <AdPreview
                    adData  = {adData}
                    logoUrl = {logoUrl}
                  />

                  <ActionsWrapper>
                    <Button
                      variant   = "outlined"
                      onClick   = {onCancel}
                      fullWidth = {width === 'xs'}
                    >
                      Cancel
                    </Button>

                    <Button
                      type      = "submit"
                      isLoading = {isSubmitting}
                      disabled  = {!isValid || !dirty || isSubmitting}
                      fullWidth = {width === 'xs'}
                    >
                      {!values.id ? 'Create Ad' : 'Save Ad'}
                    </Button>
                  </ActionsWrapper>
                </Grid>
              )}
            </Grid>
          </Wrapper>
        </GridGray>

        {(width === 'lg' || width === 'xl') && (
          <Grid item xs={12} sm={4}>
            <AdPreview
              adData  = {adData}
              logoUrl = {logoUrl}
            />

            <ActionsWrapper>
              <Button
                variant = "outlined"
                onClick = {onCancel}
              >
                Cancel
              </Button>

              <Button
                type      = "submit"
                isLoading = {isSubmitting}
                disabled  = {!isValid || !dirty || isSubmitting}
              >
                {!values.id ? 'Create Ad' : 'Save Ad'}
              </Button>
            </ActionsWrapper>
          </Grid>
        )}
      </Grid>
    </Form>
  );
};
